import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_CONFIG } from "./seoConfig";

/**
 * SEOHead component
 * Renders all SEO meta tags, Open Graph, Twitter Card, structured data, and canonical.
 *
 * Props:
 *   title         - page title
 *   description   - meta description
 *   keywords      - comma-separated keywords
 *   canonical     - canonical URL
 *   ogImage       - Open Graph image URL
 *   ogType        - "website" | "product" (default: "website")
 *   noIndex       - if true, adds noindex,nofollow
 *   structuredData - array of JSON-LD objects to inject
 */
const SEOHead = ({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogType = "website",
  noIndex = false,
  structuredData = [],
}) => {
  const resolvedTitle = title || SITE_CONFIG.name;
  const resolvedDescription =
    description ||
    "RealFlame manufactures premium PNG stoves and burners with modern design, durability, and energy efficiency.";
  const resolvedImage = ogImage || `${SITE_CONFIG.url}/android-chrome-512x512.png`;
  const resolvedCanonical = canonical || SITE_CONFIG.url;

  return (
    <Helmet>
      {/* ── Primary Meta ────────────────────────────── */}
      <html lang="en" />
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta
        name="robots"
        content={noIndex ? "noindex, nofollow" : "index, follow"}
      />
      <meta name="author" content={SITE_CONFIG.name} />
      <meta name="theme-color" content="#d4af37" />
      <link rel="canonical" href={resolvedCanonical} />

      {/* ── Open Graph ──────────────────────────────── */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_CONFIG.name} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:image:alt" content={resolvedTitle} />
      <meta property="og:url" content={resolvedCanonical} />
      <meta property="og:locale" content="en_IN" />

      {/* ── Twitter Card ────────────────────────────── */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE_CONFIG.twitter} />
      <meta name="twitter:creator" content={SITE_CONFIG.twitter} />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />
      <meta name="twitter:image:alt" content={resolvedTitle} />

      {/* ── Mobile / PWA ────────────────────────────── */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-title" content={SITE_CONFIG.name} />
      <meta name="application-name" content={SITE_CONFIG.name} />

      {/* ── Structured Data (JSON-LD) ───────────────── */}
      {structuredData.map((schema, index) => (
        <script
          key={`jsonld-${index}`}
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </Helmet>
  );
};

export default SEOHead;
